/**
 * A agenda de contatos de manutenção, blindagem e serviços da frota, em tabela.
 *
 * Papel: mostra uma linha por contato, com o nome, o serviço que presta, o
 * telefone, o e-mail, o endereço e a observação.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira.
 *
 * Só o nome é obrigatório no cadastro, e por isso o travessão aparece com
 * frequência nas demais colunas: célula vazia numa tabela lê-se como dado que
 * faltou preencher, e aqui a ausência é comum e legítima.
 */
import { type Oficina } from '@concierge/shared';

import { formatarTelefone } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaOficinasProps {
  oficinas: Oficina[];
  /** Chamado com o contato da linha; ausente esconde a coluna de ações. */
  onEditar?: (oficina: Oficina) => void;
  /** Chamado com o contato a excluir; a confirmação é de quem monta. */
  onRemover?: (oficina: Oficina) => void;
}

/**
 * A tabela de contatos.
 *
 * @param oficinas - Os contatos a mostrar, já em ordem.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por contato.
 */
export function TabelaOficinas({ oficinas, onEditar, onRemover }: TabelaOficinasProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome/Oficina</TableHead>
          <TableHead>Serviço</TableHead>
          <TableHead>Telefone</TableHead>
          <TableHead>E-mail</TableHead>
          <TableHead>Endereço</TableHead>
          <TableHead>Obs.</TableHead>
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {oficinas.map((oficina) => (
          <TableRow key={oficina.id}>
            <TableCell className="text-texto-medio">{oficina.nome}</TableCell>
            <TableCell>{oficina.servico ?? '—'}</TableCell>
            <TableCell>
              {oficina.telefone === null ? '—' : formatarTelefone(oficina.telefone)}
            </TableCell>
            <TableCell>{oficina.email ?? '—'}</TableCell>
            <TableCell className="text-[12.5px] text-texto-fraco">
              {oficina.endereco ?? '—'}
            </TableCell>
            <TableCell className="text-[12.5px] text-texto-fraco">
              {oficina.observacao ?? '—'}
            </TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(oficina)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(oficina)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
